/**
 * THE FLOW RAIL — minute Δ/vega greek-flow, the MM rail's §FLOW section (blueprint §1, R80 P3).
 *
 * SOURCE: `/api/es/greekflow` (BUILDER-ak, `@5f5eda4`, keyed GK-mm `bh=5d837143`) over the capture
 * BUILDER-aj closed as chain ② — 855+ rows service-side after a 57h gap.
 *
 * ⛔⛔ ONE SERIES, NEVER TWO — AND THE PANE IS THE WRONG PLACE TO ENFORCE IT.
 * OPTIONSCHAT-j's binding (`bh=65654c6b`, discharged live `bh=8db20d92`): greek-flow is POOLED
 * across SPX/SPXW. His warning is why this note exists: ***A POOLED FAMILY RENDERED AS TWO SERIES
 * DOES NOT LOOK BROKEN — IT LOOKS LIKE AGREEMENT***, two lines tracking each other perfectly, and
 * NO READER AND NO ARM IN THIS FILE CAN SEE IT. The route enforces single-root by construction
 * (`root` required, two-root ask is a 400, envelope echoes what it served). This module therefore
 * takes ONE root's bars and has no concept of merging: there is no code path here to blend, which
 * is the only defence a display layer can honestly offer.
 *
 * ⛔ TWO ABSENCE SHAPES, NEVER COLLAPSED, and they mean different things:
 *   · `null` value  — the minute exists and the measure does not. A HOLE. Never 0.
 *   · `ambiguous`   — the ROUTE refused the bucket: an unresolved revision tie it would not pick
 *                     between. Counted apart from a hole, because "we have no value" and "we
 *                     declined to choose between two" are different admissions.
 *
 * ⛔ THE CLOCK IS NAMED, BECAUSE THREE LIVE ON THIS TABLE. j measured them 1.3h apart
 * (`bh=d925c91b`): max(minute_ts) 20:59Z · max(vendor_ts) 20:59Z · max(receipt_ts) 22:17Z.
 * ⇒ ***A CHIP READING "flow 8m old" WITHOUT NAMING ITS CLOCK IS WRONG BY OVER AN HOUR AND LOOKS
 * PRECISE.*** The route's `age_s` is `now - receipt_ts` — OUR KNOWLEDGE CLOCK — and this pane says
 * so on its face rather than letting the reader supply the wrong one.
 */

/** One bar as `/api/es/greekflow` serves it. Every measure is nullable; `ambiguous` is the refusal. */
export interface GreekFlowBar {
  minute_ts: string;
  ambiguous: boolean;
  dir_delta_flow: number | null;
  dir_vega_flow: number | null;
  transactions: number | null;
  volume: number | null;
}

/** One plotted rung. `value === null` ⇒ NOT DRAWN. */
export interface FlowRung {
  t: number;
  delta: number | null;
  vega: number | null;
}

export interface FlowRail {
  rungs: FlowRung[];
  /** Buckets the ROUTE refused (unresolved revision tie) — not drawn, counted apart from holes. */
  refused: number;
  /** Minutes present whose measure is NULL — drawn as holes, never as zero. */
  holes: number;
  /** Plot range over DRAWN delta values only; contains zero (the rail is signed). */
  deltaRange: { min: number; max: number } | null;
  note: string | null;
}

const ms = (iso: string): number => new Date(iso).getTime();

export function buildFlowRail(bars: GreekFlowBar[]): FlowRail {
  const rungs: FlowRung[] = [];
  let refused = 0;
  let holes = 0;
  let min = Infinity;
  let max = -Infinity;

  for (const b of bars) {
    const t = ms(b.minute_ts);
    if (b.ambiguous) {
      // The route declined to pick between revisions. Drawing EITHER would be choosing for it.
      rungs.push({ t, delta: null, vega: null });
      refused += 1;
      continue;
    }
    const delta = typeof b.dir_delta_flow === "number" && Number.isFinite(b.dir_delta_flow) ? b.dir_delta_flow : null;
    const vega = typeof b.dir_vega_flow === "number" && Number.isFinite(b.dir_vega_flow) ? b.dir_vega_flow : null;
    if (delta === null) holes += 1;
    if (delta !== null) {
      if (delta < min) min = delta;
      if (delta > max) max = delta;
    }
    rungs.push({ t, delta, vega });
  }

  // Signed rail ⇒ the domain MUST contain zero, or "all buying" and "all selling" render alike.
  let deltaRange: { min: number; max: number } | null = null;
  if (min !== Infinity) {
    const lo = Math.min(min, 0);
    const hi = Math.max(max, 0);
    const pad = (hi - lo) * 0.08 || 1;
    deltaRange = { min: lo - pad, max: hi + pad };
  }

  const parts: string[] = [];
  if (refused > 0) {
    parts.push(
      `${refused} minute${refused === 1 ? "" : "s"} REFUSED by the route (unresolved revision) — not drawn, not guessed`,
    );
  }
  if (holes > 0) {
    parts.push(`${holes} minute${holes === 1 ? "" : "s"} carry no Δ measure — left EMPTY, never zero`);
  }

  return { rungs, refused, holes, deltaRange, note: parts.length ? parts.join(" · ") : null };
}

/** One positioned bar. `x` is derived from TIME, never from the array index. */
export interface FlowGeom {
  bars: { x: number; y: number; h: number; w: number; pos: boolean }[];
  /** SVG path for the vega line, BROKEN at every hole/refusal. "" when nothing is drawable. */
  vegaPath: string;
  zeroY: number;
  /** Vega's own signed domain — it is a different magnitude from delta and cannot share one. */
  vegaRange: { min: number; max: number } | null;
}

/**
 * GEOMETRY IS A PURE FUNCTION SO AN ARM CAN SEE IT.
 *
 * ⛔ THIS EXISTS BECAUSE GK-nn's M1 PASSED. He deleted the ENTIRE bar-render loop from the pane and
 * all 22 suites stayed GREEN: every arm I had written guarded the PROMISE (the face label) and not
 * one guarded the DELIVERY. ⇒ ***A PANE'S ARMS MUST BE ABLE TO SEE THAT IT DREW SOMETHING.***
 *
 * ⛔ X IS TIME, NOT INDEX (F2). Positioning by array index makes an ABSENT MINUTE OCCUPY ZERO
 * WIDTH, so the 57-hour capture gap this pane was built over would render as adjacent minutes.
 * That is exactly the defect `seriesGap.ts` documents for the price chart — ***THE GAP VANISHES***
 * — and I diagnosed it on the halt yesterday and then built it here myself.
 */
export function flowRailGeometry(rail: FlowRail, W: number, H: number, pad = 4): FlowGeom {
  const drawn = rail.rungs.filter((r) => r.delta !== null || r.vega !== null);
  if (!rail.deltaRange || drawn.length === 0) return { bars: [], vegaPath: "", zeroY: H / 2, vegaRange: null };

  const t0 = rail.rungs[0].t;
  const t1 = rail.rungs[rail.rungs.length - 1].t;
  const span = t1 - t0 || 1;
  const xOf = (t: number) => pad + ((t - t0) / span) * (W - 2 * pad);
  const yOf = (v: number, r: { min: number; max: number }) =>
    r.max === r.min ? H / 2 : H - pad - ((v - r.min) / (r.max - r.min)) * (H - 2 * pad);

  const zeroY = yOf(0, rail.deltaRange);
  // Minute width from the MEDIAN gap, so one outlier gap cannot make every bar hairline-thin.
  const gaps = rail.rungs.slice(1).map((r, i) => r.t - rail.rungs[i].t).filter((g) => g > 0).sort((a, b) => a - b);
  const step = gaps.length ? gaps[Math.floor(gaps.length / 2)] : span;
  const w = Math.max(1, (step / span) * (W - 2 * pad) - 1);

  const bars = rail.rungs
    .filter((r) => r.delta !== null)
    .map((r) => {
      const y = yOf(r.delta as number, rail.deltaRange!);
      return {
        x: xOf(r.t) - w / 2,
        y: (r.delta as number) >= 0 ? y : zeroY,
        h: Math.max(1, Math.abs(zeroY - y)),
        w,
        pos: (r.delta as number) >= 0,
      };
    });

  const vs = rail.rungs.map((r) => r.vega).filter((v): v is number => v !== null);
  let vegaRange: { min: number; max: number } | null = null;
  let vegaPath = "";
  if (vs.length) {
    const lo = Math.min(...vs, 0);
    const hi = Math.max(...vs, 0);
    const vpad = (hi - lo) * 0.08 || 1;
    vegaRange = { min: lo - vpad, max: hi + vpad };
    let pen = false;
    for (const r of rail.rungs) {
      if (r.vega === null) {
        // A HOLE BREAKS THE LINE. Bridging it would draw a value across minutes nobody measured.
        pen = false;
        continue;
      }
      vegaPath += `${pen ? "L" : "M"}${xOf(r.t).toFixed(1)},${yOf(r.vega, vegaRange).toFixed(1)}`;
      pen = true;
    }
  }
  return { bars, vegaPath, zeroY, vegaRange };
}

/**
 * The pane's face. It names the SERIES, the ROOT it was served, and the CLOCK its age uses —
 * all three, because each has cost this estate a defect: the series (mis-label law), the root
 * (j's pooled binding), the clock (three clocks 1.3h apart on one table).
 */
export function flowRailLabel(root: string | null): string {
  return `FLOW · dir Δ/vega · ${root ?? "root UNDECLARED"} · age = receipt`;
}
